import { useCallback } from 'react';
import { useAppDispatch, useAppStore } from 'store/hooks';
import { postFunnelEvents } from 'features/funnel-events/actions';
import { saveNextStepEvents } from 'features/funnel-events/utils';
import { selectEmploymentStatus, selectFundingSource, selectHasAnnualIncome, selectHasNetworth, selectHasOnboardedInvest, } from 'features/invest/reducers/selectors';
import { selectHidePotNameScreen } from 'features/wealth/selectors';
import { getMainStackRoutes, getStepsToRevisit, recordOnboardingStep } from 'features/wealth/utils/onboardingHistory';
import { selectPrimaryAddressForGB } from 'reducers/selectors';
import { OnboardingFlow } from 'utils/types/navigation';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { showUnavailableOnWeb } from 'utils/webGate';
import { selectPotProductFromProductId } from '../reducers/selectors';
export const getNextScreenForPotsOnboarding = (onboardingState, potsOnboarding, potProduct, hasPrimaryAddress, hasAddressWithKYCStatus, taxId, hasAnnualIncome, hasNetWorth, hasFundingSource, hasEmploymentStatus, connectedAccount, hidePotNameScreen, hasOnboardedInvest, 
// Steps after the current one that the user has already seen; shown again even when their data is complete.
stepsToRevisit = new Set()) => {
    const revisit = (...steps) => steps.some((step) => stepsToRevisit.has(step));
    if (!potsOnboarding?.productId) {
        return {
            name: 'SelectPotType',
            params: { isPotsOnboarding: true },
        };
    }
    if (!hasOnboardedInvest) {
        if (!onboardingState?.screen || revisit('InvestPersonalDetails')) {
            return {
                name: 'InvestPersonalDetails',
                params: { flow: OnboardingFlow.Pots },
            };
        }
        if ((onboardingState.screen === 'InvestAddress' ||
            !hasPrimaryAddress ||
            revisit('InvestAddress', 'AddressPostCodeScreen')) &&
            !hasAddressWithKYCStatus) {
            if (hasPrimaryAddress) {
                return {
                    name: 'InvestAddress',
                    params: { flow: OnboardingFlow.Pots },
                };
            }
            return {
                name: 'AddressPostCodeScreen',
                params: {
                    skipGetAddress: true,
                    flow: OnboardingFlow.Pots,
                },
            };
        }
        if (potProduct?.isProductCashISA && (!taxId || revisit('InvestInsurance'))) {
            return {
                name: 'InvestInsurance',
                params: { flow: OnboardingFlow.Pots },
            };
        }
        if (!hasAnnualIncome || revisit('InvestAnnualIncome')) {
            return {
                name: 'InvestAnnualIncome',
                params: { flow: OnboardingFlow.Pots },
            };
        }
        if (!hasNetWorth || revisit('InvestNetWorth')) {
            return {
                name: 'InvestNetWorth',
                params: { flow: OnboardingFlow.Pots },
            };
        }
        if (!hasFundingSource || revisit('InvestFundingSource')) {
            return {
                name: 'InvestFundingSource',
                params: { flow: OnboardingFlow.Pots },
            };
        }
        if (!hasEmploymentStatus || revisit('InvestEmploymentStatus')) {
            return {
                name: 'InvestEmploymentStatus',
                params: { flow: OnboardingFlow.Pots },
            };
        }
    }
    if (!potsOnboarding.createPotsParams || revisit('CreatePot', 'ReviewAndConfirm')) {
        if (hidePotNameScreen && potProduct) {
            return {
                name: 'ReviewAndConfirm',
                params: {
                    createPotParams: {
                        selectedEmoji: '💰',
                        name: potProduct.name,
                        type: potProduct.type,
                    },
                    productId: potsOnboarding.productId,
                },
            };
        }
        return {
            name: 'CreatePot',
            params: { productId: potsOnboarding.productId },
        };
    }
    if (potsOnboarding.initialDepositAmount === undefined || revisit('PotInitialDepositAmountScreen')) {
        return {
            name: 'PotInitialDepositAmountScreen',
            params: {},
        };
    }
    if (!potsOnboarding.collectionFrequency || revisit('AutosaveOnboardingIntroScreen')) {
        return {
            name: 'AutosaveOnboardingIntroScreen',
            params: {},
        };
    }
    if (typeof potsOnboarding.automated === 'undefined' ||
        typeof potsOnboarding.contributionAmount === 'undefined' ||
        typeof potsOnboarding.roundUpMultiplier === 'undefined' ||
        revisit('AutosaveKnowContributionAmount')) {
        return {
            name: 'AutosaveKnowContributionAmount',
            params: {},
        };
    }
    if (typeof potsOnboarding.allowOverdraft === 'undefined' || revisit('AutosaveOverdraftSettings')) {
        return {
            name: 'AutosaveOverdraftSettings',
            params: {},
        };
    }
    if (!onboardingState?.fundingAccount || revisit('SelectFundingAccount', 'AutosaveSelectFundingAccount')) {
        if (!connectedAccount) {
            return {
                name: 'SelectFundingAccount',
                params: { isPotsOnboarding: true },
            };
        }
        return {
            name: 'AutosaveSelectFundingAccount',
            params: {
                account: connectedAccount,
            },
        };
    }
    return {
        name: 'PotsOnboardingReviewDepositAndAutosaveInfoScreen',
        params: { isPotsOnboarding: true },
    };
};
const useNavigateToNextScreenForPotsOnboarding = () => {
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const navigateToNextScreen = useCallback(() => {
        if (showUnavailableOnWeb('emma://save?tab=pots', 'Open a savings pot')) {
            return;
        }
        const { connectedAccount } = store.getState().invest;
        const userTaxId = store.getState().user.user.taxId;
        const hasNetWorth = selectHasNetworth(store.getState());
        const potsOnboarding = store.getState().pots.onboarding;
        const onboardingState = store.getState().invest.onboarding;
        const fundingSource = selectFundingSource(store.getState());
        const hasAnnualIncome = selectHasAnnualIncome(store.getState());
        const employmentStatus = selectEmploymentStatus(store.getState());
        const primaryAddress = selectPrimaryAddressForGB(store.getState());
        const hidePotNameScreen = selectHidePotNameScreen(store.getState());
        const hasOnboardedInvest = selectHasOnboardedInvest(store.getState());
        const potProduct = selectPotProductFromProductId(store.getState(), potsOnboarding?.productId);
        const stackRoutes = getMainStackRoutes(navigation);
        const nextScreenObj = getNextScreenForPotsOnboarding(onboardingState, potsOnboarding, potProduct, !!primaryAddress, !!primaryAddress?.kycStatus, userTaxId, hasAnnualIncome, hasNetWorth, !!fundingSource, !!employmentStatus, connectedAccount, hidePotNameScreen, hasOnboardedInvest, getStepsToRevisit('pots', stackRoutes));
        const funnelEvent = saveNextStepEvents[nextScreenObj.name];
        if (funnelEvent) {
            dispatch(postFunnelEvents([funnelEvent]));
        }
        recordOnboardingStep('pots', stackRoutes, nextScreenObj.name);
        navigation.navigate(nextScreenObj);
    }, []);
    return navigateToNextScreen;
};
export default useNavigateToNextScreenForPotsOnboarding;
